import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function MockInterviewHistory() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getToken } = useAuth();
  const { showError } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/mock-interview/history?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load history');
      }

      const data = await response.json();
      setInterviews(data.interviews || []);
    } catch (error) {
      showError('Failed to load interview history');
    } finally {
      setLoading(false);
    }
  };

  const getDecisionBadge = (decision) => {
    const styles = {
      'hire': { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', text: 'Hire' },
      'maybe': { bg: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', text: 'Maybe' },
      'no_hire': { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', text: 'No Hire' }
    };
    return styles[decision] || styles['maybe'];
  };

  const getScoreColor = (score) => {
    if (score >= 75) return '#22c55e';
    if (score >= 60) return '#fbbf24';
    return '#ef4444';
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid var(--border-subtle)',
            borderTop: '3px solid var(--indigo-500)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: 'var(--text-tertiary)' }}>Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', padding: '40px 20px' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/interview-prep')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-tertiary)',
              fontSize: '14px',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            ← Back to Interview Prep
          </button>
          <h1 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px' }}>
            Mock Interview History
          </h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '16px' }}>
            Review your past interview performances and track improvement
          </p>
        </div>

        {/* Empty State */}
        {interviews.length === 0 ? (
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-light)',
            borderRadius: '16px',
            padding: '64px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'var(--gradient-primary)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>
              No Interview History Yet
            </h3>
            <p style={{ color: 'var(--text-tertiary)', fontSize: '16px', marginBottom: '24px' }}>
              Complete your first mock interview to see results here
            </p>
            <button
              onClick={() => navigate('/interview-prep')}
              style={{
                padding: '14px 32px',
                background: 'var(--gradient-primary)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              Start Your First Mock Interview
            </button>
          </div>
        ) : (
          <>
            {/* Stats Summary */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '20px',
              marginBottom: '32px'
            }}>
              <div style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-light)',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                  {interviews.length}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                  Total Interviews
                </div>
              </div>

              <div style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-light)',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#22c55e', marginBottom: '8px' }}>
                  {Math.round(interviews.reduce((sum, i) => sum + (i.score || 0), 0) / interviews.length) || 0}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                  Average Score
                </div>
              </div>

              <div style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-light)',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#fbbf24', marginBottom: '8px' }}>
                  {interviews.filter(i => i.decision === 'hire').length}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                  Would Hire
                </div>
              </div>
            </div>

            {/* Interview List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {interviews.map(interview => {
                const decisionBadge = getDecisionBadge(interview.decision);
                return (
                  <div
                    key={interview.id}
                    style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-light)',
                      borderRadius: '12px',
                      padding: '24px',
                      transition: 'all 0.2s',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/mock-interview/${interview.id}/results`)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'var(--bg-elevated)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'var(--bg-surface)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {/* Header Row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div>
                        <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>
                          {interview.company}
                        </h3>
                        <p style={{ fontSize: '15px', color: 'var(--text-tertiary)' }}>
                          {interview.role}
                        </p>
                      </div>
                      <div style={{
                        padding: '8px 16px',
                        background: decisionBadge.bg,
                        color: decisionBadge.color,
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '700'
                      }}>
                        {decisionBadge.text}
                      </div>
                    </div>

                    {/* Metrics Row */}
                    <div style={{ display: 'flex', gap: '24px', alignItems: 'center', paddingTop: '16px', borderTop: '1px solid var(--border-light)' }}>
                      <div>
                        <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                          Score
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: getScoreColor(interview.score) }}>
                          {interview.score}/100
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                          Duration
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
                          {interview.duration} min
                        </div>
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                          Date
                        </div>
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--text-primary)' }}>
                          {new Date(interview.date).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/mock-interview/${interview.id}/results`);
                        }}
                        style={{
                          padding: '10px 20px',
                          background: 'rgba(99, 102, 241, 0.2)',
                          color: 'var(--indigo-500)',
                          border: '1px solid rgba(99, 102, 241, 0.3)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = 'rgba(99, 102, 241, 0.3)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)';
                        }}
                      >
                        View Details →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Back Button */}
        <div style={{ marginTop: '32px', textAlign: 'center' }}>
          <button
            onClick={() => navigate('/interview-prep')}
            style={{
              padding: '14px 32px',
              background: 'var(--border-light)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            Back to Interview Prep
          </button>
        </div>
      </div>
    </div>
  );
}

export default MockInterviewHistory;
